import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Bookmark, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Saved filter presets bar.
 *
 * Props:
 *   page          — string key identifying the page (e.g., "appointments")
 *   currentFilters — JSON-serializable object representing current filter state
 *   onApply        — callback(filters) when the user picks a preset
 */
export default function FilterPresetsBar({ page, currentFilters, onApply }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get("/filter-presets", { params: { page } });
      setPresets(r.data || []);
    } catch (e) {
      // Quiet — non-critical; user can still use the page without presets.
    } finally { setLoading(false); }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page]);

  function applyPresetById(id) {
    setSelectedId(id);
    if (!id) return;
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    onApply?.(p.filters);
    toast.success(`Applied "${p.name}"`);
  }

  async function savePreset() {
    const name = saveName.trim();
    if (!name) {
      toast.error("Give the preset a name");
      return;
    }
    setSaveBusy(true);
    try {
      const r = await api.post("/filter-presets", {
        page, name, filters: currentFilters || {},
      });
      const newId = r.data?.id;
      await refresh();
      setSaveOpen(false);
      setSaveName("");
      if (newId) setSelectedId(newId);
      toast.success(`Saved "${name}"`);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaveBusy(false); }
  }

  async function deleteSelected() {
    if (!selectedId) {
      toast.error("Pick a preset to delete first");
      return;
    }
    const p = presets.find((x) => x.id === selectedId);
    if (!p) return;
    if (!confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.delete(`/filter-presets/${selectedId}`);
      setSelectedId("");
      await refresh();
      toast.success(`Deleted "${p.name}"`);
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="flex items-center gap-2" data-testid={`filter-presets-${page}`}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-zinc-500">
        <Bookmark className="h-3.5 w-3.5" /> Presets
      </div>
      <Select value={selectedId} onValueChange={applyPresetById}>
        <SelectTrigger
          className="h-8 w-[220px] border-zinc-300 text-sm"
          data-testid="filter-presets-picker"
        >
          <SelectValue placeholder={loading ? "Loading…" : presets.length ? "— Apply a preset —" : "No saved presets"} />
        </SelectTrigger>
        <SelectContent>
          {presets.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-zinc-500 italic">No saved presets yet</div>
          )}
          {presets.map((p) => (
            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="outline"
        size="sm"
        className="h-8 border-zinc-300 hover:bg-zinc-100"
        onClick={() => setSaveOpen(true)}
        data-testid="filter-presets-save-btn"
      >
        <Save className="h-3.5 w-3.5 mr-1" /> Save current
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 px-2 text-zinc-500 hover:text-red-700"
        onClick={deleteSelected}
        disabled={!selectedId}
        data-testid="filter-presets-delete-btn"
        title={selectedId ? "Delete the selected preset" : "Pick a preset first"}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save filter preset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-zinc-600">
              Saves the current filter values (date range, calendar, type, etc.) so you can
              recall them with one click. Saving with the same name overwrites the previous version.
            </p>
            <div>
              <label className="text-xs font-medium text-zinc-700">Preset name</label>
              <Input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g., Q1 2026, Nick's calendar"
                className="mt-1 border-zinc-300"
                autoFocus
                data-testid="filter-presets-name-input"
                onKeyDown={(e) => { if (e.key === "Enter") savePreset(); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button
              onClick={savePreset}
              disabled={saveBusy}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="filter-presets-save-confirm"
            >
              {saveBusy ? "Saving…" : "Save preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
